import { ReactElement, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getRun } from '../../api/runs';
import { sessionIdFromPath } from '../../utils/sessionIdFromPath';
import { resolveRunEpisode, setEpisodeDbUpload } from '../../api/episodes';
import { useReprocess } from '../../hooks/useReprocess';
import { useProfile } from '../../hooks/useProfile';
import RunProgressIndicator from '../../components/RunProgressIndicator';
import { BsFileZip, BsFileZipFill } from 'react-icons/bs';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import {
  HiOutlineArrowPath,
  HiOutlineCloudArrowUp,
  HiOutlineMagnifyingGlass,
  HiOutlineFlag,
} from 'react-icons/hi2';
import { Run } from '../../types/Run';

interface RunActionsProps {
  runId: string;
  /** The loaded run record — gates blob-sourced actions (source_episode_id). */
  run?: Run | null;
}

export default function RunActions({ runId, run }: RunActionsProps): ReactElement {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: { show_blob_controls } } = useProfile();
  const { handleReprocess, isProcessing, getRunProgress, isRunReprocessing, watchRuns } = useReprocess();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Reload recovery: if this run is already mid-reprocess (e.g. the page was
  // refreshed), pick the in-flight state back up by polling /progress.
  useEffect(() => {
    if (runId) watchRuns([runId]);
  }, [runId, watchRuns]);

  const runProgress = getRunProgress(runId);
  const runReprocessing = isRunReprocessing(runId);

  const handleReprocessRun = async (): Promise<void> => {
    try {
      const invalidate = () => {
        queryClient.invalidateQueries({
          queryKey: ['runAndCalibration', runId],
        });
      };
      await handleReprocess(invalidate, [{ id: runId } as any]);
    } catch (error) {
      console.error('Error reprocessing run:', error);
    }
    setIsMenuOpen(false);
  };

  const handleRequestEpisodeUpload = async (): Promise<void> => {
    try {
      const sessionId = sessionIdFromPath(location.pathname) ?? null;
      const episodeId = await resolveRunEpisode(run ?? {}, sessionId);
      if (!episodeId) {
        toast.error('No unique Nimbus episode found for this run — use Options → Nimbus Recordings on the session');
        return;
      }
      await setEpisodeDbUpload(episodeId, 'requested');
      toast.success('Recording upload requested — the phone will upload at next app start');
    } catch (error) {
      toast.error(`Request failed: ${(error as Error).message}`);
    }
    setIsMenuOpen(false);
  };

  const handleDownloadZip = async (scope: 'download_raw_zip' | 'download_blob_zip'): Promise<void> => {
    try {
      // Get the S3 download URL from the API
      const response = await getRun(runId, `scope=${scope}`);

      // The response should contain the S3 URL - navigate to it
      if (response) {
        // 1. Create a temporary anchor element
        const link = document.createElement('a');
        link.href = response;

        // 2. Suggest a filename for the download (optional but recommended)
        // This extracts the filename from the S3 URL before the '?'
        link.setAttribute('download', response.split('?')[0].split('/').pop());

        // 3. Append to the document, click it, and then remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // if (response.data) {
      //   window.location.href = response.data;
      // }
    } catch (error) {
      console.error('Error getting download URL:', error);
      toast.error(`Download failed: ${(error as Error).message}`);
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="cr-relative cr-flex cr-items-center cr-gap-2">
      <RunProgressIndicator info={runProgress} variant="full" />
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="cr-rounded-lg cr-p-2 cr-text-gray-600 cr-transition-colors hover:cr-bg-gray-100"
        aria-label="More actions"
      >
        <HiOutlineDotsVertical className="cr-h-5 cr-w-5" />
      </button>

      {isMenuOpen && (
        <>
          <div className="cr-fixed cr-inset-0 cr-z-10" onClick={() => setIsMenuOpen(false)} />
          <div className="cr-absolute cr-right-0 cr-top-full cr-z-20 cr-mt-2 cr-w-56 cr-rounded-md cr-bg-white cr-shadow-lg cr-ring-1 cr-ring-black cr-ring-opacity-5">
            <div className="cr-py-1">
              <button
                onClick={() => handleReprocessRun()}
                disabled={isProcessing || runReprocessing}
                className="cr-flex cr-w-full cr-items-center cr-gap-3 cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100 disabled:cr-opacity-50 disabled:cr-cursor-not-allowed"
              >
                <HiOutlineArrowPath className={`cr-h-5 cr-w-5 cr-text-blue-600 ${runReprocessing ? 'cr-animate-spin' : ''}`} />
                <span>
                  {runReprocessing ? 'Reprocessing...' : 'Reprocess Run'}
                </span>
              </button>
              <button
                onClick={() => handleDownloadZip('download_raw_zip')}
                className="cr-flex cr-w-full cr-items-center cr-gap-3 cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
              >
                <BsFileZip className="cr-h-5 cr-w-5 cr-text-blue-600" />
                <span>Download Raw ZIP</span>
              </button>
              {show_blob_controls && run?.source_episode_id && (
                <button
                  onClick={() => handleDownloadZip('download_blob_zip')}
                  title="Download the run's stream build — the zip cut from the uploaded Nimbus episode stream"
                  className="cr-flex cr-w-full cr-items-center cr-gap-3 cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
                >
                  <BsFileZipFill className="cr-h-5 cr-w-5 cr-text-blue-600" />
                  <span>Download Stream Build</span>
                </button>
              )}
              {show_blob_controls && (
                <button
                  onClick={handleRequestEpisodeUpload}
                  title="Flag this run's Nimbus episode so the phone uploads its full recording .db at next app start"
                  className="cr-flex cr-w-full cr-items-center cr-gap-3 cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
                >
                  <HiOutlineCloudArrowUp className="cr-h-5 cr-w-5 cr-text-blue-600" />
                  <span>Request NIMBUS Backup Data</span>
                </button>
              )}
              <button
                onClick={() => {
                  // Lap parsing is reachable for any session type from here, not
                  // just the ones that expose the tab. Navigate to the nested
                  // lap-parsing sub-route (RunView recognises it even when its
                  // tab chip is hidden).
                  const pathParts = location.pathname.split('/');
                  const runIndex = pathParts.findIndex((p) => p === 'runs');
                  const basePath = pathParts.slice(0, runIndex + 2).join('/');
                  navigate(`${basePath}/lap-parsing`);
                  setIsMenuOpen(false);
                }}
                className="cr-flex cr-w-full cr-items-center cr-gap-3 cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
              >
                <HiOutlineFlag className="cr-h-5 cr-w-5 cr-text-blue-600" />
                <span>Lap Parsing</span>
              </button>
              <button
                onClick={() => {
                  // Strip current tab from path, append /diagnostic
                  const pathParts = location.pathname.split('/');
                  const runIndex = pathParts.findIndex((p) => p === 'runs');
                  const basePath = pathParts.slice(0, runIndex + 2).join('/');
                  navigate(`${basePath}/diagnostic`);
                  setIsMenuOpen(false);
                }}
                className="cr-flex cr-w-full cr-items-center cr-gap-3 cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
              >
                <HiOutlineMagnifyingGlass className="cr-h-5 cr-w-5 cr-text-blue-600" />
                <span>Run Diagnostic</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
